import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Chip, 
  Container, 
  Grid, 
  IconButton, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TextField, 
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Alert,
  AlertTitle,
  CircularProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { 
  Add, 
  ClearAll, 
  Delete, 
  Edit, 
  FilterList, 
  InboxOutlined, 
  Refresh, 
  Search, 
  Visibility,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import shipmentService from '../../services/shipmentService';
import { getDrivers } from '../../services/driverService';
import zoneService from '../../services/zoneService';
import customerService from '../../services/customerService';
import { 
  Shipment, 
  ShipmentStatus, 
  ShipmentFilterParams,
  createShipmentWithHelpers,
  ShipmentWithHelpers
} from '../../types/shipment';
import { User } from '../../types/user';
import { Driver } from '../../types/driver';
import { Zone } from '../../types/zone';

// Extend the Shipment type with our display properties
type ProcessedShipment = Shipment & {
  displayTrackingNumber: string;
  displayCustomerName: string;
  displayDriverName: string;
  displayZoneName: string;
}

console.log('Rendering ShipmentsList component');

interface ShipmentsListState {
  shipments: Shipment[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  total: number;
  searchTerm: string;
  statusFilter: ShipmentStatus | 'all';
  error: string | null;
  drivers: Record<string, Driver>;
  zones: Record<string, Zone>;
  customers: Record<string, any>;
}

const ShipmentsList: React.FC = () => {
  console.log('ShipmentsList - Component mounted');
  
  // Initialize state with proper types
  const [state, setState] = useState<ShipmentsListState>({
    shipments: [],
    loading: true,
    page: 0,
    rowsPerPage: 10,
    total: 0,
    searchTerm: '',
    statusFilter: 'all',
    error: null,
    drivers: {},
    zones: {},
    customers: {},
  });

  // Process shipments to add display properties with driver names and zone names
  const processedShipments: ProcessedShipment[] = state.shipments.map(shipment => {
    const driverId = shipment.driver_id || shipment.driverId;
    const zoneId = shipment.zone_id || shipment.zoneId;
    
    // Handle customer name display with actual customer data
    const customerId = shipment.customer_id || shipment.customerId;
    let customerName: string;
    
    if (customerId === null || customerId === undefined) {
      customerName = 'No Customer';
    } else {
      const customer = state.customers[customerId.toString()] || state.customers[customerId];
      if (customer) {
        customerName = customer.company_name || customer.contact_person || `Customer #${customerId}`;
      } else {
        // Customer ID exists but not found in our data (might be loading or error)
        customerName = `Customer #${customerId}`;
      }
    }
      
    // Handle driver name display with better logic
    let driverName: string;
    
    if (driverId === null || driverId === undefined) {
      // No driver assigned
      driverName = 'Unassigned';
    } else {
      // Try both string and direct lookup for driver ID
      const driver = state.drivers[driverId.toString()] || state.drivers[driverId];
      if (driver) {
        driverName = driver.name;
      } else {
        // Driver ID exists but not found in our data (might be loading or error)
        driverName = `Driver #${driverId}`;
      }
    }
    
    // Handle zone name display
    let zoneName: string;
    
    if (zoneId === null || zoneId === undefined) {
      // No zone assigned
      zoneName = 'No Zone';
    } else {
      // Try both string and direct lookup for zone ID
      const zone = state.zones[zoneId.toString()] || state.zones[zoneId];
      if (zone) {
        zoneName = zone.name;
      } else {
        // Zone ID exists but not found in our data (might be loading or error)
        zoneName = `Zone #${zoneId}`;
      }
    }
    
    return {
      ...shipment,
      displayTrackingNumber: shipment.trackingNumber || shipment.tracking_number || 'N/A',
      displayCustomerName: customerName,
      displayDriverName: driverName,
      displayZoneName: zoneName
    } as ProcessedShipment;
  });

  // Destructure state for easier access
  const { 
    shipments, 
    loading, 
    page, 
    rowsPerPage, 
    total, 
    searchTerm, 
    statusFilter, 
    error,
    drivers
  } = state;

  // Track per-shipment status update loading state
  const [statusUpdating, setStatusUpdating] = useState<Record<string, boolean>>({});

  // Navigation and theming hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  // Status colors mapping for the Chip component
  const statusColorMap: Record<ShipmentStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    pending: 'warning',
    in_transit: 'info',
    out_for_delivery: 'primary',
    delivered: 'success',
    exception: 'error',
    returned: 'secondary',
  };
  
  // Get translated status text
  const getStatusText = (status: ShipmentStatus) => {
    return t(`shipments.statuses.${status}`, status);
  };

  // Fetch drivers data
  const fetchDrivers = useCallback(async () => {
    try {
      console.log('[ShipmentsList] Fetching drivers...');
      
      // Fetch drivers
      const driversData = await getDrivers();
      const driversMap = driversData.reduce((acc: Record<string, Driver>, driver: Driver) => {
        acc[driver.id] = driver;
        return acc;
      }, {});
      
      setState(prev => ({
        ...prev,
        drivers: driversMap
      }));
      
      console.log('[ShipmentsList] Loaded drivers:', {
        count: Object.keys(driversMap).length,
        driverIds: Object.keys(driversMap),
        drivers: driversMap
      });
    } catch (error) {
      console.error('[ShipmentsList] Error fetching drivers:', error);
      // Don't fail the whole component if drivers fail to load
    }
  }, []);

  // Fetch zones data
  const fetchZones = useCallback(async () => {
    try {
      console.log('[ShipmentsList] Fetching zones...');
      
      // Fetch zones
      const zonesData = await zoneService.getZones();
      const zonesArray: Zone[] = Array.isArray(zonesData) ? zonesData : (zonesData as any)?.data || [];
      const zonesMap = zonesArray.reduce((acc: Record<string, Zone>, zone: Zone) => {
        acc[zone.id] = zone;
        return acc;
      }, {});
      
      setState(prev => ({
        ...prev,
        zones: zonesMap
      }));
      
      console.log('[ShipmentsList] Loaded zones:', {
        count: Object.keys(zonesMap).length,
        zoneIds: Object.keys(zonesMap),
        zones: zonesMap
      });
    } catch (error) {
      console.error('[ShipmentsList] Error fetching zones:', error);
      // Don't fail the whole component if zones fail to load
    }
  }, []);

  // Fetch customers data
  const fetchCustomers = useCallback(async () => {
    try {
      console.log('[ShipmentsList] Fetching customers...');
      
      // Fetch customers
      const customersData = await customerService.getCustomers();
      const customersArray = Array.isArray(customersData) ? customersData : (customersData as any)?.data || [];
      const customersMap = customersArray.reduce((acc: Record<string, any>, customer: any) => {
        acc[customer.user_id] = customer;
        return acc;
      }, {});
      
      setState(prev => ({
        ...prev,
        customers: customersMap
      }));
      
      console.log('[ShipmentsList] Loaded customers:', {
        count: Object.keys(customersMap).length,
        customerIds: Object.keys(customersMap),
        customers: customersMap
      });
    } catch (error) {
      console.error('[ShipmentsList] Error fetching customers:', error);
      // Don't fail the whole component if customers fail to load
    }
  }, []);

  const fetchShipments = useCallback(async () => {
    console.log('[ShipmentsList] Fetching shipments...');
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      // Prepare query parameters
      const params: ShipmentFilterParams = {
        page: page + 1, // Backend is 1-based
        limit: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        _sort: 'created_at:desc', // Ensure consistent ordering
      };
      
      console.log('[ShipmentsList] Fetch params:', params);
      const response = await shipmentService.getShipments(params);
      
      // Handle both array and object responses
      let shipmentsData: Shipment[] = [];
      let totalCount = 0;
      
      if (Array.isArray(response)) {
        // If response is an array, use it directly
        shipmentsData = response;
        totalCount = response.length;
      } else if (response && typeof response === 'object') {
        // Handle paginated response with data and total
        shipmentsData = response.data || [];
        totalCount = response.total || shipmentsData.length;
      }
      
      console.log(`[ShipmentsList] Fetched ${shipmentsData.length} of ${totalCount} shipments`);
      
      // Use the helper methods from ShipmentWithHelpers to display consistent data
      const getShipmentDisplayData = (shipment: Shipment) => {
        const shipmentWithHelpers = createShipmentWithHelpers(shipment);
        return {
          ...shipmentWithHelpers,
          displayTrackingNumber: shipmentWithHelpers.getTrackingNumber(),
          displayCustomerName: shipmentWithHelpers.getCustomerName(),
          displayDriverName: shipmentWithHelpers.getDriverName(),
          displayZoneName: shipmentWithHelpers.getZoneName()
        };
      };

      // Process the shipments data using our helper methods
      const processedShipments = shipmentsData.map(shipment => getShipmentDisplayData(shipment));
      
      setState(prev => ({
        ...prev,
        shipments: processedShipments,
        total: totalCount,
        loading: false,
      }));
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load shipments';
      console.error('[ShipmentsList] Error fetching shipments:', errorMessage);
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
        shipments: [], // Clear previous data on error
      }));
    }
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  // Load drivers, zones, and customers on component mount
  useEffect(() => {
    fetchDrivers();
    fetchZones();
    fetchCustomers();
  }, [fetchDrivers, fetchZones, fetchCustomers]);

  useEffect(() => {
    console.log('useEffect - fetchShipments dependency changed');
    console.log('Current state:', { page, rowsPerPage, searchTerm, statusFilter });
    
    let isMounted = true;
    
    const loadData = async () => {
      console.log('[ShipmentsList] Loading shipments...');
      try {
        await fetchShipments();
      } catch (error) {
        console.error('Error in loadData:', error);
        if (isMounted) {
          setState(prev => ({
            ...prev,
            error: 'Failed to load shipments. Please try again.',
            loading: false,
            shipments: []
          }));
        }
      }
    };
    
    // Always fetch data when dependencies change, regardless of loading state
    loadData();
    
    return () => {
      isMounted = false;
      console.log('useEffect cleanup - component unmounting');
    };
  }, [fetchShipments]);



  const handleRefresh = useCallback(() => {
    console.log('[ShipmentsList] Refreshing shipments, drivers, zones, and customers...');
    setState(prev => ({ ...prev, shipments: [], drivers: {}, zones: {}, customers: {} })); // Clear data to show loading state
    fetchDrivers(); // Refresh drivers first
    fetchZones(); // Refresh zones
    fetchCustomers(); // Refresh customers
    fetchShipments(); // Then refresh shipments
  }, [fetchDrivers, fetchZones, fetchCustomers, fetchShipments]);

  // Auto-refresh when user returns to the page (e.g., after editing a shipment)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('[ShipmentsList] Page became visible - refreshing data');
        handleRefresh();
      }
    };

    const handleFocus = () => {
      console.log('[ShipmentsList] Window gained focus - refreshing data');
      handleRefresh();
    };

    // Listen for page visibility changes (tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Listen for window focus events (returning from another app/window)
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [handleRefresh]);

  const handleChangePage = (event: unknown, newPage: number) => {
    console.log('[ShipmentsList] Page changed to:', newPage);
    setState(prev => ({
      ...prev,
      page: newPage,
      // Reset to first page if data changes
      ...(newPage === 0 && { shipments: [] })
    }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // The search is already handled by the useEffect that depends on searchTerm
    // This just prevents the form from submitting and refreshing the page
    console.log('[ShipmentsList] Search submitted:', searchTerm);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    console.log('[ShipmentsList] Search term changed:', newSearchTerm);
    
    setState(prev => ({
      ...prev,
      searchTerm: newSearchTerm,
      page: 0, // Reset to first page when searching
      shipments: [], // Clear data to show loading state
    }));
  };

  const handleStatusFilterChange = (e: any) => {
    setState(prev => ({ ...prev, statusFilter: e.target.value, page: 0 }));
  };

  const handleClearFilters = () => {
    setState(prev => ({
      ...prev,
      searchTerm: '',
      statusFilter: 'all',
      page: 0,
    }));
  };

  const getStatusLabel = (status: ShipmentStatus) => {
    const statusMap: Record<ShipmentStatus, string> = {
      pending: t('shipments.statuses.pending'),
      in_transit: t('shipments.statuses.in_transit'),
      out_for_delivery: t('shipments.statuses.out_for_delivery'),
      delivered: t('shipments.statuses.delivered'),
      exception: t('shipments.statuses.exception'),
      returned: t('shipments.statuses.returned')
    };
    return statusMap[status] || status;
  };

  console.log('Rendering - loading:', loading, 'shipments:', shipments);

  // Show loading state with more detailed message
  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {t('shipments.messages.loading')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('common.loading')}
          </Typography>
          {searchTerm && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {t('common.searchingFor')}: "{searchTerm}"
            </Typography>
          )}
        </Box>
      </Container>
    );
  }

  // Show error state with more details
  if (error) {
    return (
      <Container maxWidth="xl">
        <Box p={3}>
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={fetchShipments}
                startIcon={<Refresh />}
              >
                {t('common.retry')}
              </Button>
            }
          >
            <AlertTitle>{t('shipments.messages.error')}</AlertTitle>
            {error}
            <Box component="div" sx={{ mt: 1, fontSize: '0.875rem' }}>
              <Typography variant="body2" component="div">
                <strong>{t('common.page')}:</strong> {page + 1}<br />
                <strong>{t('common.itemsPerPage')}:</strong> {rowsPerPage}<br />
                {searchTerm && <><strong>{t('common.searchTerm')}:</strong> {searchTerm}<br /></>}
                {statusFilter !== 'all' && <><strong>{t('shipments.fields.status')}:</strong> {getStatusLabel(statusFilter as ShipmentStatus)}<br /></>}
              </Typography>
            </Box>
          </Alert>
          <Box mt={2}>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={() => {
                setState(prev => ({
                  ...prev,
                  page: 0,
                  searchTerm: '',
                  statusFilter: 'all'
                }));
                fetchShipments();
              }}
              startIcon={<ClearAll />}
              sx={{ mr: 2 }}
            >
              Reset All Filters
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={fetchShipments}
              startIcon={<Refresh />}
            >
              Try Again
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // Show empty state with more guidance
  if (shipments.length === 0) {
    return (
      <Container maxWidth="xl">
        <Box p={4} textAlign="center">
          <InboxOutlined 
            color="disabled" 
            sx={{ fontSize: 64, opacity: 0.5, mb: 2 }} 
          />
          <Typography variant="h5" gutterBottom>
            No Shipments Found
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {searchTerm || statusFilter !== 'all' 
              ? 'No shipments match your current search and filter criteria.'
              : 'No shipments have been created yet.'}
          </Typography>
          
          {(searchTerm || statusFilter !== 'all') && (
            <Box mb={3}>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={() => {
                  setState(prev => ({
                    ...prev,
                    searchTerm: '',
                    statusFilter: 'all',
                    page: 0
                  }));
                  fetchShipments();
                }}
                startIcon={<ClearAll />}
              >
                Clear All Filters
              </Button>
            </Box>
          )}
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Add />}
            onClick={() => navigate('/shipments/new')}
            sx={{ mt: 2 }}
          >
            Create New Shipment
          </Button>
          
          <Box mt={3}>
            <Typography variant="body2" color="textSecondary">
              Having trouble? Try refreshing the page or check your connection.
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  // Main content
  console.log('Rendering shipments:', shipments);
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', mb: 2, borderRadius: 1 }}>
        <Typography variant="body1" color="textSecondary">
          Debug: Component is rendering with {shipments.length} shipments
        </Typography>
      </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Shipments</Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                size="small"
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => navigate('/shipments/new')}
              >
                Add Shipment
              </Button>
            </Box>
          </Box>

        <Card>
          <CardContent>
            <form onSubmit={handleSearch}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={t('shipments.filter.searchPlaceholder')}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: <Search color="action" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="status-filter-label">
                      {t('shipments.filter.filters.status')}
                    </InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={statusFilter}
                      onChange={(e) => setState(prev => ({ ...prev, statusFilter: e.target.value as ShipmentStatus | 'all' }))}
                      label={t('shipments.filter.filters.status')}
                    >
                      <MenuItem value="all">
                        {t('shipments.filter.filters.all')}
                      </MenuItem>
                      {Object.keys(statusColorMap).map((status) => (
                        <MenuItem key={status} value={status}>
                          {getStatusLabel(status as ShipmentStatus)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    type="submit"
                    startIcon={<FilterList />}
                  >
                    {t('common.applyFilters')}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        <Card sx={{ mt: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('shipments.fields.trackingNumber')}</TableCell>
                  <TableCell>{t('shipments.fields.origin')}</TableCell>
                  <TableCell>{t('shipments.fields.destination')}</TableCell>
                  <TableCell>{t('shipments.fields.status')}</TableCell>
                  <TableCell>{t('shipments.fields.customer')}</TableCell>
                  <TableCell>{t('shipments.fields.driver')}</TableCell>
                  <TableCell>{t('shipments.fields.zone')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : processedShipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {t('common.noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  processedShipments.map((shipment) => (
                    <TableRow key={shipment.id} hover>
                      <TableCell>{shipment.displayTrackingNumber}</TableCell>
                      <TableCell>{shipment.origin}</TableCell>
                      <TableCell>{shipment.destination}</TableCell>
                      <TableCell>
                        {(() => { const tn = String(shipment.trackingNumber || shipment.tracking_number); return statusUpdating[tn]; })() ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Select
                            size="small"
                            value={shipment.status}
                            onChange={async (e) => {
                              const newStatus = e.target.value as ShipmentStatus;
                              const tn = String(shipment.trackingNumber || shipment.tracking_number);
                              setStatusUpdating(prev => ({ ...prev, [tn]: true }));
                              try {
                                await shipmentService.updateShipmentStatus(String(shipment.trackingNumber || shipment.tracking_number), { status: newStatus });
                                setState(prev => ({
                                  ...prev,
                                  shipments: prev.shipments.map(s =>
                                    (s.trackingNumber || s.tracking_number) === (shipment.trackingNumber || shipment.tracking_number) ? { ...s, status: newStatus } : s
                                  )
                                }));
                                enqueueSnackbar(t('shipments.messages.updated'), { variant: 'success' });
                              } catch (error: any) {
                                console.error('Error updating status:', error);
                                enqueueSnackbar(error.response?.data?.message || t('shipments.messages.updateError'), { variant: 'error' });
                              } finally {
                                const tn2 = String(shipment.trackingNumber || shipment.tracking_number);
                                 setStatusUpdating(prev => ({ ...prev, [tn2]: false }));
                              }
                            }}
                            sx={{ minWidth: 140 }}
                          >
                            {Object.keys(statusColorMap).map((statusKey) => (
                              <MenuItem key={statusKey} value={statusKey}>
                                {getStatusText(statusKey as ShipmentStatus)}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>{shipment.displayCustomerName}</TableCell>
                      <TableCell>{shipment.displayDriverName}</TableCell>
                      <TableCell>{shipment.displayZoneName}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/shipments/${shipment.trackingNumber}`)}
                            startIcon={<VisibilityIcon fontSize="small" />}
                          >
                            {t('common.view')}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={() => navigate(`/shipments/edit/${shipment.trackingNumber}`)}
                            startIcon={<EditIcon fontSize="small" />}
                          >
                            {t('common.edit')}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            disabled={!shipment || !shipment.trackingNumber}
                            onClick={async () => {
                              if (!shipment || !shipment.trackingNumber) {
                                enqueueSnackbar('Invalid shipment or missing tracking number', { variant: 'error' });
                                return;
                              }
                              
                              if (window.confirm(t('shipments.messages.confirmDelete'))) {
                                try {
                                  await shipmentService.deleteShipment(shipment.trackingNumber);
                                  enqueueSnackbar(t('shipments.messages.deleted'), { variant: 'success' });
                                  fetchShipments(); // Refresh the list
                                } catch (error: any) {
                                  console.error('Error deleting shipment:', error);
                                  const errorMessage = error.response?.data?.message || error.message || t('shipments.messages.deleteError');
                                  enqueueSnackbar(errorMessage, { 
                                    variant: 'error',
                                    autoHideDuration: 5000
                                  });
                                }
                              }
                            }}
                            startIcon={<DeleteIcon fontSize="small" />}
                          >
                            {t('common.delete')}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} ${t('common.of')} ${count}`
            }
          />
        </Card>
    </Container>
  );
};

export default ShipmentsList;
